import React, { useMemo, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import CategoryIcon from 'components/CategoryIcon';
import { categories as CATEGORY_TREE } from 'utils/categories';
import { getMacroName, getMicroName } from '../../utils/getCategoryData';

// ---- helpers: map micro -> macro, normalize to grouped shape ----
const buildMicroToMacroMap = () => {
  const map = new Map();
  const getSubs = (m) =>
    m?.micro || m?.microCategories || m?.children || m?.items || [];
  CATEGORY_TREE.forEach((macro) => {
    getSubs(macro).forEach((micro) => map.set(micro.id, macro.id));
  });
  return map;
};

const normalizeCategories = (input) => {
  if (!input) return [];

  if (Array.isArray(input)) {
    const byMacro = new Map();
    input.forEach((x) => {
      if (!x) return;
      const macro = x.macroCategory ?? x.macroCategoryId;
      if (typeof macro !== 'number') return;
      const micro = x.microCategory ?? x.microCategoryIds;
      if (!byMacro.has(macro)) byMacro.set(macro, new Set());
      if (Array.isArray(micro)) micro.forEach((m) => byMacro.get(macro).add(m));
    });
    return Array.from(byMacro.entries()).map(([macroCategory, microSet]) => ({
      macroCategory,
      microCategory: Array.from(microSet),
    }));
  }

  if (typeof input === 'object') {
    const microToMacro = buildMicroToMacroMap();
    const macros = Array.isArray(input.macros) ? input.macros : [];
    const micros = Array.isArray(input.micros) ? input.micros : [];

    const grouped = new Map();

    macros.forEach((m) => grouped.set(m, []));

    micros.forEach((mid) => {
      const parent = microToMacro.get(mid);
      if (!parent) return;
      if (!grouped.has(parent)) grouped.set(parent, []);
      grouped.get(parent).push(mid);
    });

    return Array.from(grouped.entries()).map(
      ([macroCategory, microCategory]) => ({
        macroCategory,
        microCategory: [...new Set(microCategory)],
      })
    );
  }

  return [];
};

const CategoryList = ({
  categories,
  onlyMacro,
  withLabel,
  isVertical = false,
  macroIconClass = 'w-7 h-7',
  microIconClass = 'w-6 h-6',
  containerClass,
}) => {
  const grouped = useMemo(() => normalizeCategories(categories), [categories]);

  // ---- floating tooltip state (single tooltip for the whole list) ----
  const [tip, setTip] = useState(null); // { text, rect, variant }

  const showTip = useCallback((e, text, variant = 'macro') => {
    if (!text) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setTip({ text, rect, variant });
  }, []);

  const hideTip = useCallback(() => setTip(null), []);

  const baseContainer = onlyMacro
    ? 'flex flex-row flex-wrap items-center gap-2 overflow-visible'
    : 'flex flex-col overflow-visible';

  const tooltipNode =
    tip && typeof document !== 'undefined'
      ? createPortal(
          (() => {
            const { rect } = tip;
            const margin = 8;
            const approxTooltipH = 40; // good enough
            const fitsBelow =
              rect.bottom + margin + approxTooltipH < window.innerHeight;
            const top = fitsBelow ? rect.bottom + margin : rect.top - margin;
            const translateY = fitsBelow ? '0%' : '-100%';
            const left = rect.left + rect.width / 2;

            const base =
              'pointer-events-none fixed z-[9999] text-xs rounded-lg px-2 py-1 shadow-md ' +
              'max-w-[min(220px,calc(100vw-16px))] min-w-[140px]';

            const theme =
              tip.variant === 'micro'
                ? 'bg-black text-white'
                : 'bg-gray-600 text-white';

            return (
              <div
                className={`${base} ${theme}`}
                style={{
                  left,
                  top,
                  transform: `translate(-50%, ${translateY})`,
                }}
              >
                <div className="text-center leading-snug whitespace-normal break-words">
                  {tip.text}
                </div>
              </div>
            );
          })(),
          document.body
        )
      : null;

  if (!grouped.length) return null;

  return (
    <>
      <div
        className={
          containerClass ?? (isVertical ? 'flex flex-col gap-3' : baseContainer)
        }
      >
        {grouped.map((cat) => {
          const micros = Array.isArray(cat?.microCategory)
            ? cat.microCategory
            : [];
          const macroText = getMacroName(cat?.macroCategory) ?? '';

          return (
            <div key={cat.macroCategory} className="flex flex-col gap-2">
              {/* Macro */}
              <div
                className={
                  isVertical
                    ? 'flex flex-row items-center gap-3'
                    : 'inline-flex'
                }
                onMouseEnter={
                  !isVertical && withLabel
                    ? (e) => showTip(e, macroText, 'macro')
                    : undefined
                }
                onMouseLeave={!isVertical && withLabel ? hideTip : undefined}
              >
                <CategoryIcon
                  macro={cat?.macroCategory}
                  className={`shrink-0 ${isVertical ? 'w-12 h-12' : macroIconClass}`}
                />
                {isVertical && macroText && (
                  <span className="font-medium">{macroText}</span>
                )}
              </div>

              {/* Micros */}
              {!onlyMacro && micros.length > 0 && (
                <div
                  className={
                    isVertical
                      ? 'flex flex-col gap-2 ml-6'
                      : 'flex flex-row flex-wrap items-center gap-2'
                  }
                >
                  {micros.map((micro) => {
                    const microText = getMicroName(micro) ?? '';
                    return (
                      <div
                        key={`${cat.macroCategory}-${micro}`}
                        className={
                          isVertical
                            ? 'flex flex-row items-center gap-3'
                            : 'inline-flex'
                        }
                        onMouseEnter={
                          !isVertical && withLabel
                            ? (e) => showTip(e, microText, 'micro')
                            : undefined
                        }
                        onMouseLeave={
                          !isVertical && withLabel ? hideTip : undefined
                        }
                      >
                        <CategoryIcon
                          macro={cat?.macroCategory}
                          microCat={micro}
                          className={`shrink-0 ${isVertical ? 'w-10 h-10' : microIconClass}`}
                        />
                        {isVertical && microText && (
                          <span className="text-sm text-gray-700">
                            {microText}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {tooltipNode}
    </>
  );
};

export default CategoryList;
